import React, { useRef, useState } from "react";
import { MessageSquareText } from "lucide-react";

import { Input, Select, Switch, Tip } from "@/components/ui";
import type { LeavesQuery } from "@/graphql/generated";
import {
  useLeafFlagSaves,
  useLeafSaveStatus,
  useLeafSavedValue,
} from "@/hooks/useLeafFlagSaves";
import { cn } from "@/lib/utils";

import { LeafHelpPanel } from "./LeafHelpPanel";
import { LeafSaveSlot } from "./LeafSaveSlot";
import { moveLabelFocus } from "./labelFocus";
import { AREA, LEAF_GRID } from "./leafGrid";

export type Leaf = LeavesQuery["leaves"][number];

export interface LeafRowProps {
  leaf: Leaf;
  onRequiredChange: (leaf: Leaf, next: boolean) => Promise<void>;
}

// ponytail: waits for the engine to send its InputType list. Intent: leaf input types.
const INPUT_TYPES = [
  ["", "From marketplace"],
  ["text", "Text"],
  ["textarea", "Text area"],
  ["number", "Number"],
  ["boolean", "Yes/No"],
  ["many", "Many values"],
  ["date", "Date"],
] as const;

const FAILED = "border-rose-400 ring-2 ring-rose-400/20 dark:border-rose-400";

export const LeafRow: React.FC<LeafRowProps> = ({ leaf, onRequiredChange }) => {
  const { save } = useLeafFlagSaves();
  const [helpOpen, setHelpOpen] = useState(false);
  const helpButton = useRef<HTMLButtonElement>(null);
  const helpId = `leaf-help-${leaf.path}`;
  const labelStatus = useLeafSaveStatus(leaf.path, "label");
  const typeStatus = useLeafSaveStatus(leaf.path, "inputType");
  const triedLabel = useLeafSavedValue(leaf.path, "label");
  const triedType = useLeafSavedValue(leaf.path, "inputType");
  const inputType =
    typeof triedType === "string" ? triedType : (leaf.inputType ?? "");
  const quiet = () => {};
  const commitLabel = (value: string) =>
    (value !== (leaf.label ?? "") || labelStatus === "failed") &&
    save(leaf, { label: value }).catch(quiet);
  const closeHelp = () => {
    helpButton.current?.focus();
    setHelpOpen(false);
  };

  return (
    <div
      data-leaf-row={leaf.path}
      className="border-t border-slate-200 dark:border-slate-800"
    >
      <div
        className={cn(
          LEAF_GRID,
          "py-2.5 min-[1400px]:min-h-[52px] min-[1400px]:py-2",
        )}
      >
        <div className={cn(AREA.leaf, "min-w-0")}>
          <span className="font-mono text-[13px] break-all">{leaf.leaf}</span>
          {leaf.anchor && (
            <span className="ml-2 text-xs font-medium text-indigo-600 dark:text-indigo-300">
              anchor
            </span>
          )}
          {leaf.helpText && (
            <p className="mt-0.5 hidden truncate text-xs text-slate-500 dark:text-slate-400 min-[1400px]:block">
              {leaf.helpText}
            </p>
          )}
        </div>
        <div className={AREA.label}>
          <Input
            data-leaf-label
            defaultValue={
              typeof triedLabel === "string" ? triedLabel : (leaf.label ?? "")
            }
            placeholder={leaf.leaf.replace(/_/g, " ")}
            aria-label={`Label: ${leaf.leaf}`}
            onBlur={(e) => commitLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
              e.preventDefault();
              const moved = moveLabelFocus(
                e.currentTarget,
                e.shiftKey ? -1 : 1,
              );
              if (!moved) commitLabel(e.currentTarget.value);
            }}
            className={cn(labelStatus === "failed" && FAILED)}
          />
        </div>
        <div className={AREA.type}>
          <Select
            value={inputType}
            aria-label={`Input type: ${leaf.leaf}`}
            onChange={(e) =>
              save(leaf, { inputType: e.target.value }).catch(quiet)
            }
            className={cn(
              "[&_option]:text-slate-900 dark:[&_option]:text-slate-100",
              inputType === "" &&
                "[&_select]:text-slate-400 dark:[&_select]:text-slate-400",
              typeStatus === "failed" &&
                "[&_select]:border-rose-400 [&_select]:ring-2 [&_select]:ring-rose-400/20 dark:[&_select]:border-rose-400",
            )}
          >
            {INPUT_TYPES.map(([value, text]) => (
              <option key={value} value={value}>
                {text}
              </option>
            ))}
          </Select>
        </div>
        <div className={AREA.req}>
          <Switch
            checked={leaf.alwaysRequired}
            aria-label={`Required: ${leaf.leaf}`}
            onCheckedChange={(next) => onRequiredChange(leaf, next)}
          />
        </div>
        <div className={AREA.ps}>
          <Switch
            checked={leaf.perStore}
            aria-label={`Per store: ${leaf.leaf}`}
            onCheckedChange={(next) => save(leaf, { perStore: next })}
          />
        </div>
        <div className={AREA.help}>
          <Tip label="Help text">
            <button
              ref={helpButton}
              type="button"
              aria-label={`Help text: ${leaf.leaf}`}
              aria-expanded={helpOpen}
              aria-controls={helpId}
              onClick={() => setHelpOpen((o) => !o)}
              className={cn(
                "inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors duration-150 motion-safe:active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500",
                helpOpen
                  ? "border border-indigo-500 bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300"
                  : "text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800",
              )}
            >
              <MessageSquareText className="h-4 w-4" aria-hidden />
            </button>
          </Tip>
        </div>
        <LeafSaveSlot path={leaf.path} name={leaf.leaf} />
      </div>
      {helpOpen && (
        <LeafHelpPanel leaf={leaf} id={helpId} onClose={closeHelp} />
      )}
    </div>
  );
};
